/**
 * Adapted from 21st.dev "Shape Landing Hero" (kokonutd / id 524).
 * Product copy and OpenGateway orange brand applied for SaaS homepage.
 */
import { motion } from "framer-motion";
import { Circle, ArrowRight, Code2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Show, SignUpButton } from "@clerk/react";
import { cn } from "@/lib/utils";

function ElegantShape({
  className,
  delay = 0,
  width = 400,
  height = 100,
  rotate = 0,
  y = 15,
  gradient = "from-white/[0.08]",
}: {
  className?: string;
  delay?: number;
  width?: number;
  height?: number;
  rotate?: number;
  y?: number;
  gradient?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -150, rotate: rotate - 15 }}
      animate={{ opacity: 1, y: 0, rotate }}
      transition={{
        duration: 2.4,
        delay,
        ease: [0.23, 0.86, 0.39, 0.96],
        opacity: { duration: 1.2 },
      }}
      className={cn("absolute", className)}
    >
      <motion.div
        animate={{ y: [0, y, 0] }}
        transition={{
          duration: 12,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
        style={{ width, height }}
        className="relative"
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-gradient-to-r to-transparent",
            gradient,
            "border-2 border-white/[0.15] backdrop-blur-[2px]",
            "shadow-[0_8px_32px_0_rgba(255,255,255,0.1)]",
            "after:absolute after:inset-0 after:rounded-full",
            "after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]"
          )}
        />
      </motion.div>
    </motion.div>
  );
}

export function HeroGeometric({
  badge = "OpenGateway Cloud",
  title1 = "Multi-agent rooms",
  title2 = "that actually ship",
}: {
  badge?: string;
  title1?: string;
  title2?: string;
}) {
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.4 + i * 0.15,
        ease: [0.25, 0.4, 0.25, 1] as const,
      },
    }),
  };

  return (
    <div className="relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden bg-[#030303]">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.08] via-transparent to-violet-500/[0.08] blur-3xl" />

      <div className="absolute inset-0 overflow-hidden">
        <ElegantShape
          delay={0.3}
          width={600}
          height={140}
          rotate={12}
          gradient="from-orange-500/[0.18]"
          className="top-[12%] left-[-8%] md:top-[18%] md:left-[-2%]"
        />
        <ElegantShape
          delay={0.5}
          width={500}
          height={120}
          rotate={-15}
          gradient="from-violet-500/[0.16]"
          className="top-[65%] right-[-8%] md:top-[68%] md:right-[2%]"
        />
        <ElegantShape
          delay={0.4}
          width={320}
          height={90}
          rotate={-8}
          gradient="from-amber-400/[0.14]"
          className="bottom-[8%] left-[8%] md:bottom-[12%] md:left-[16%]"
        />
        <ElegantShape
          delay={0.6}
          width={220}
          height={70}
          rotate={20}
          gradient="from-cyan-400/[0.12]"
          className="top-[8%] right-[12%] md:top-[12%] md:right-[18%]"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/[0.1] bg-white/[0.04] px-3 py-1.5 md:mb-12"
          >
            <Circle className="h-2 w-2 fill-orange-400/90" />
            <span className="text-sm tracking-wide text-white/70">{badge}</span>
          </motion.div>

          <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible">
            <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-6xl md:mb-8 md:text-7xl">
              <span className="bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent">
                {title1}
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-300 via-white/90 to-violet-300 bg-clip-text text-transparent">
                {title2}
              </span>
            </h1>
          </motion.div>

          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="visible">
            <p className="mx-auto mb-10 max-w-xl px-2 text-base leading-relaxed font-light tracking-wide text-white/50 sm:text-lg md:text-xl">
              One hub for Grok, Claude Code, Cursor, Codex, and humans. Rooms,
              tasks, MCP radio, and Live Ops — hosted for you on{" "}
              <span className="text-white/70">opengateways.xyz</span>.
            </p>
          </motion.div>

          <motion.div
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Show when="signed-out">
              <SignUpButton mode="modal" forceRedirectUrl="/app">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_40px_-8px_rgba(249,115,22,0.8)] transition hover:bg-orange-400"
                >
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <Link
                to="/app"
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-semibold text-white shadow-[0_0_40px_-8px_rgba(249,115,22,0.8)] transition hover:bg-orange-400"
              >
                Open dashboard
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Show>
            <a
              href="https://github.com/mrdulasolutions/open-gateway"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-medium text-white/80 transition hover:bg-white/[0.08]"
            >
              <Code2 className="h-4 w-4" />
              Open source core
            </a>
          </motion.div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/80]" />
    </div>
  );
}
